import { Component, OnDestroy, OnInit, Optional } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialogRef } from '@angular/material/dialog';
import type { HubConnection } from '@microsoft/signalr';
import { ContactService } from './contact.service';
import { ContactModel } from './contact.model';
import { ContactImport } from '../const';
import { environment } from '../../../../environments/environment';
import { consts } from '../../../consts';
import { NotificationService } from '../../../modules_v2/shared/services/notification.service';

const IMPORT_SUCCESS_MESSAGE = 'Todos los contactos han sido añadidos correctamente.';

@Component({
    selector: 'convfact-contact-import',
    templateUrl: './contact-import.component.html',
    styleUrls: ['./contact-import.component.css'],
    standalone: false
})
export class ContactImportComponent implements OnInit, OnDestroy {

  constructor(
    private contactService: ContactService,
    private notify: NotificationService,
    private router: Router,
    @Optional() public dialogRef: MatDialogRef<ContactImportComponent, boolean>
  ) { }

  dataReady = false;
  working = false;
  workingMessage = '';
  dataSource: ContactModel[] = [];
  headerTable: any[] = ContactImport.headerTable;
  selectedFileName: string | null = null;
  progress = 0;
  hubConnection: HubConnection;
  private destroyed = false;

  get isDialog(): boolean {
    return !!this.dialogRef;
  }

  get rowsWithWarnings(): number {
    return this.dataSource.filter(row => row.importWarnings && row.importWarnings.length > 0).length;
  }

  async ngOnInit(): Promise<void> {
    const signalR = await import('@microsoft/signalr');
    if (this.destroyed) { return; }
    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(environment.apiUrl + consts.HUB_PROGRESS)
      .build();

    this.hubConnection.on('ReceiveProgress', (data: number) => {
      this.progress = data;
    });

    this.hubConnection.start().catch(() => undefined);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.hubConnection) {
      this.hubConnection.stop().catch(() => undefined);
    }
  }

  close(result = false): void {
    if (this.working) { return; }
    if (this.dialogRef) {
      this.dialogRef.close(result);
      return;
    }
    this.router.navigate(['/contact']);
  }

  downloadTemplate(): void {
    window.open('/assets/templates/plantilla_contactos.xlsx', '_blank');
  }

  downloadDemoTemplate(): void {
    window.open('/assets/templates/plantilla_contactos_demo.xlsx', '_blank');
  }

  cellValue(column: any, row: any): string {
    const value = row[column.field];
    if (value === true) { return 'Sí'; }
    if (value === false || value === null || value === undefined) { return ''; }
    return String(value);
  }

  resetFile(): void {
    this.dataReady = false;
    this.dataSource = [];
    this.selectedFileName = null;
  }

  fileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) { return; }

    const file = input.files[0];
    input.value = '';
    this.selectedFileName = file.name;

    const formData: FormData = new FormData();
    formData.append('file', file, file.name);

    this.startWorking('Procesando archivo…');
    this.contactService.importContacts(formData)
      .then(response => {
        this.dataSource = Array.isArray(response) ? response : [];
        this.dataReady = true;
        this.stopWorking();
      })
      .catch(error => {
        this.stopWorking();
        this.selectedFileName = null;
        this.notify.error(this.errorMessage(error, 'No se pudo procesar el archivo. Revisa que use la plantilla.'));
      });
  }

  addContacts(): void {
    if (!this.dataReady || !this.dataSource.length || this.working) { return; }

    this.startWorking('Importando contactos…');
    this.contactService.importData(this.dataSource)
      .then(message => {
        this.stopWorking();
        if (!message || message === IMPORT_SUCCESS_MESSAGE) {
          this.notify.ok(message || 'Contactos importados correctamente');
        } else {
          this.notify.warning(message);
        }
        this.close(true);
      })
      .catch(error => {
        this.stopWorking();
        this.notify.error(this.errorMessage(error, 'No se pudieron importar los contactos'));
      });
  }

  trackByIndex(index: number): number {
    return index;
  }

  private startWorking(message: string): void {
    this.progress = 0;
    this.workingMessage = message;
    this.working = true;
    if (this.dialogRef) { this.dialogRef.disableClose = true; }
  }

  private stopWorking(): void {
    this.working = false;
    if (this.dialogRef) { this.dialogRef.disableClose = false; }
  }

  private errorMessage(error: any, fallback: string): string {
    const body = error && error.error;
    if (body && typeof body === 'object' && body.message) { return body.message; }
    if (typeof body === 'string' && body.trim()) { return body; }
    return fallback;
  }
}
